/**
 * Pitch spelling and arithmetic.
 *
 * Pitches are spelled (letter + alteration + octave), not just MIDI numbers,
 * because harmony depends on spelling: F♯ and G♭ sound alike but behave differently.
 */

export const LETTERS = ['C', 'D', 'E', 'F', 'G', 'A', 'B'] as const;
const NATURAL_PC = [0, 2, 4, 5, 7, 9, 11];

/** A pitch class with spelling. letter: 0 = C … 6 = B. alter: -2 … +2. */
export interface PitchClass {
  letter: number;
  alter: number;
}

/** A spelled pitch in a register. */
export interface Pitch extends PitchClass {
  octave: number;
}

export const mod = (n: number, m: number): number => ((n % m) + m) % m;

export function pcNumber(pc: PitchClass): number {
  return mod(NATURAL_PC[pc.letter] + pc.alter, 12);
}

export function midiOf(p: Pitch): number {
  return 12 * (p.octave + 1) + NATURAL_PC[p.letter] + p.alter;
}

/** Parse "C4", "F#3", "Bb2", "E♭5", "Cx4" (double sharp) or a bare pitch class "F#". */
export function parsePitch(name: string): Pitch {
  const m = /^([A-Ga-g])(##|x|#|♯|bb|b|♭|𝄫|𝄪)?(-?\d+)?$/.exec(name.trim());
  if (!m) throw new Error(`Cannot parse pitch "${name}"`);
  const letter = LETTERS.indexOf(m[1].toUpperCase() as (typeof LETTERS)[number]);
  const acc = m[2] ?? '';
  const alter =
    acc === '##' || acc === 'x' || acc === '𝄪' ? 2 : acc === '#' || acc === '♯' ? 1 : acc === 'bb' || acc === '𝄫' ? -2 : acc === 'b' || acc === '♭' ? -1 : 0;
  const octave = m[3] === undefined ? 4 : parseInt(m[3], 10);
  return { letter, alter, octave };
}

export function parsePitchClass(name: string): PitchClass {
  const p = parsePitch(name.replace(/-?\d+$/, ''));
  return { letter: p.letter, alter: p.alter };
}

function accidentalText(alter: number, unicode: boolean): string {
  if (alter === 0) return '';
  if (unicode) return alter > 0 ? '♯'.repeat(alter) : '♭'.repeat(-alter);
  return alter > 0 ? '#'.repeat(alter) : 'b'.repeat(-alter);
}

export function pcName(pc: PitchClass, unicode = true): string {
  return LETTERS[pc.letter] + accidentalText(pc.alter, unicode);
}

export function pitchName(p: Pitch, unicode = false): string {
  return pcName(p, unicode) + p.octave;
}

/** Absolute diatonic step index (C0 = 0, D0 = 1 … C1 = 7). */
export function diatonicIndex(p: Pitch): number {
  return p.letter + 7 * p.octave;
}

/** Move a pitch by a number of diatonic steps and semitones, keeping spelling correct. */
export function transposePitch(p: Pitch, steps: number, semitones: number): Pitch {
  const abs = diatonicIndex(p) + steps;
  const letter = mod(abs, 7);
  const octave = Math.floor(abs / 7);
  const target = midiOf(p) + semitones;
  const natural = 12 * (octave + 1) + NATURAL_PC[letter];
  return { letter, alter: target - natural, octave };
}

/** Interval from a to b: diatonic steps and semitones (b minus a). */
export function intervalBetween(a: Pitch, b: Pitch): { steps: number; semitones: number } {
  return { steps: diatonicIndex(b) - diatonicIndex(a), semitones: midiOf(b) - midiOf(a) };
}

/** Spell a pitch class given a letter and a target pitch-class number. */
export function spellOnLetter(letter: number, pcNum: number): PitchClass {
  const l = mod(letter, 7);
  let alter = mod(pcNum - NATURAL_PC[l], 12);
  if (alter > 6) alter -= 12;
  return { letter: l, alter };
}

export function samePc(a: PitchClass, b: PitchClass): boolean {
  return a.letter === b.letter && a.alter === b.alter;
}

export function midiToFrequency(midi: number, a4 = 440): number {
  return a4 * Math.pow(2, (midi - 69) / 12);
}

/** Fallback spelling for a bare MIDI number (sharps), used only when no spelling is known. */
export function midiToPitch(midi: number): Pitch {
  const pc = mod(midi, 12);
  const table: Array<[number, number]> = [
    [0, 0], [0, 1], [1, 0], [1, 1], [2, 0], [3, 0], [3, 1], [4, 0], [4, 1], [5, 0], [5, 1], [6, 0],
  ];
  const [letter, alter] = table[pc];
  return { letter, alter, octave: Math.floor(midi / 12) - 1 };
}
